/**
 * ComboSystem — combo-tier table + lookup helper.
 *
 * Replaces the per-game `COMBO_TIERS` const that horse-dash, puff-up,
 * and tap-tug each defined identically. The default table runs from
 * "no combo" through "ON FIRE!" with growing score multipliers and
 * increasingly hot colors. Games can pass their own table to override.
 *
 * Each tier:
 *   { min: <combo count to enter>,
 *     mult: <score multiplier while in this tier>,
 *     label: <text shown by EncouragementBanner; empty for tier 0>,
 *     color: <text + glow base color, 24-bit hex>,
 *     glow: <secondary highlight color, 24-bit hex> }
 *
 * Usage:
 *   import { ComboSystem, DEFAULT_COMBO_TIERS } from '@platform/sdk';
 *
 *   const combo = new ComboSystem();
 *
 *   // when a tap lands:
 *   player.combo += 1;
 *   const tier = combo.getTier(player.combo);  // { index, min, mult, label, color, glow }
 *   player.score += basePoints * tier.mult;
 *
 *   // detect tier-up to flash an encouragement banner:
 *   if (tier.index !== player.lastTierIndex) {
 *     player.lastTierIndex = tier.index;
 *     if (tier.label) encourage.show(tier.label, tier.color);
 *   }
 */

/**
 * Default 5-tier table. Mirrors what horse-dash / puff-up / tap-tug
 * already use today.
 */
export const DEFAULT_COMBO_TIERS = Object.freeze([
  { min: 0,  mult: 1.0,  label: '',          color: 0xffffff, glow: 0x000000 },
  { min: 4,  mult: 1.25, label: 'GOOD!',     color: 0xfacc15, glow: 0xfde68a },
  { min: 8,  mult: 1.5,  label: 'GREAT!',    color: 0xfb923c, glow: 0xfed7aa },
  { min: 12, mult: 1.8,  label: 'AWESOME!',  color: 0xec4899, glow: 0xfbcfe8 },
  { min: 16, mult: 2.2,  label: 'ON FIRE!',  color: 0xef4444, glow: 0xfecaca }
]);

export class ComboSystem {
  /**
   * @param {object} [opts]
   * @param {Array<{min: number, mult: number, label: string, color: number, glow: number}>} [opts.tiers]
   *        Custom tier table. Must be sorted by `min` ascending and start with `min: 0`.
   *        Defaults to DEFAULT_COMBO_TIERS.
   */
  constructor(opts = {}) {
    this.tiers = opts.tiers || DEFAULT_COMBO_TIERS;
  }

  /**
   * Look up the tier for a given combo count.
   * @param {number} comboCount
   * @returns {{index: number, min: number, mult: number, label: string, color: number, glow: number}}
   */
  getTier(comboCount) {
    return ComboSystem.findTier(this.tiers, comboCount);
  }

  /**
   * Score multiplier for the current combo count.
   * @param {number} comboCount
   * @returns {number}
   */
  getMultiplier(comboCount) {
    return this.getTier(comboCount).mult;
  }

  /**
   * Stateless tier lookup. Useful when you don't want to instantiate the class.
   * @param {Array} tiers   tier table (defaults to DEFAULT_COMBO_TIERS)
   * @param {number} comboCount
   * @returns {{index: number, ...}}
   */
  static findTier(tiers, comboCount) {
    const table = tiers || DEFAULT_COMBO_TIERS;
    let i = 0;
    for (let k = table.length - 1; k >= 0; k--) {
      if (comboCount >= table[k].min) { i = k; break; }
    }
    return Object.assign({ index: i }, table[i]);
  }
}
